import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAdminResourceOptions<T, R> {
  /** Busca uma página. `page` começa em 0. */
  fetchPage: (page: number) => Promise<{ data: R }>;
  /** Extrai a lista da resposta. */
  select: (response: R) => T[];
  /** Extrai o total de páginas da resposta. */
  selectPages?: (response: R) => number;
  /** Filtro aplicado a cada item carregado (ex.: esconder inativos). */
  filter?: (item: T) => boolean;
  /** Mensagem de erro exibida no `ErrorState`. */
  errorMessage?: string;
}
/**
 * Carrega uma lista paginada do backoffice com estados de carregamento, erro e
 * "carregar mais" — o padrão que estava copiado em seis telas do admin.
 */
export function useAdminResource<T, R = { pages?: number }>({
  fetchPage,
  select,
  selectPages,
  filter,
  errorMessage = 'Não foi possível carregar os dados.',
}: UseAdminResourceOptions<T, R>) {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [maxPage, setMaxPage] = useState(0);

  // Evita setState após desmontar.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyFilter = useCallback(
    (items: T[]) => (filter ? items.filter(filter) : items),
    [filter],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetchPage(0);
      if (!mounted.current) return;

      setData(applyFilter(select(response.data)));
      setMaxPage(selectPages ? selectPages(response.data) : 0);
      setCurrentPage(0);
    } catch {
      if (mounted.current) setLoadError(errorMessage);
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [fetchPage, select, selectPages, applyFilter, errorMessage]);

  const loadMore = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (nextPage >= maxPage || isLoading) return;

    setIsLoading(true);
    try {
      const response = await fetchPage(nextPage);
      if (!mounted.current) return;

      setData((previous) => previous.concat(applyFilter(select(response.data))));
      if (selectPages) setMaxPage(selectPages(response.data));
      setCurrentPage(nextPage);
    } catch {
      if (mounted.current) setLoadError(errorMessage);
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [
    currentPage,
    maxPage,
    isLoading,
    fetchPage,
    select,
    selectPages,
    applyFilter,
    errorMessage,
  ]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    setData,
    isLoading,
    loadError,
    currentPage,
    maxPage,
    // As Lambdas devolvem `pages` como QUANTIDADE (ceil(total/tamanho)), e a
    // primeira página é a 0.
    hasMore: currentPage + 1 < maxPage,
    reload: load,
    loadMore,
  };
}
